/**
 * Countdown Timer Composable
 * 
 * Provides reactive countdown timer functionality with proper cleanup
 * and user-friendly time formatting.
 * 
 * @returns {Object} Countdown timer state and methods
 */
export function useCountdownTimer() {
  // Timer state
  let timer = null
  let remainingSeconds = 0
  let isActive = false
  let callbacks = {
    onTick: null,
    onComplete: null
  }

  /**
   * Formats seconds into a user-friendly time display
   * @param {number} seconds - Number of seconds
   * @returns {string} Formatted time string (e.g., "14 minutes 32 seconds")
   */
  function formatTime(seconds) {
    if (seconds <= 0) {
      return '0 seconds'
    }

    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    const parts = []

    if (hours > 0) {
      parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`)
    }

    if (minutes > 0) {
      parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`)
    }

    if (secs > 0 || parts.length === 0) {
      parts.push(`${secs} second${secs !== 1 ? 's' : ''}`)
    }

    return parts.join(' ')
  }

  /**
   * Starts the countdown timer
   * @param {string|number|Date} targetTime - Target time (timestamp, Date object, or ISO string)
   * @param {Object} options - Timer options
   * @param {Function} options.onTick - Callback called every second with remaining seconds
   * @param {Function} options.onComplete - Callback called when timer reaches zero
   */
  function start(targetTime, options = {}) {
    // Stop any existing timer
    stop()

    // Set callbacks
    callbacks.onTick = options.onTick || null
    callbacks.onComplete = options.onComplete || null

    // Calculate initial remaining time
    const targetTimestamp = new Date(targetTime).getTime()
    const now = Date.now()
    remainingSeconds = Math.max(0, Math.floor((targetTimestamp - now) / 1000))

    // If already expired, call complete immediately
    if (remainingSeconds <= 0) {
      handleComplete()
      return
    }

    // Start the timer
    isActive = true

    // Call initial tick
    if (callbacks.onTick) {
      callbacks.onTick(remainingSeconds, formatTime(remainingSeconds))
    }

    timer = setInterval(() => {
      remainingSeconds--

      // Call tick callback
      if (callbacks.onTick) {
        callbacks.onTick(remainingSeconds, formatTime(remainingSeconds))
      }

      // Check if timer is complete
      if (remainingSeconds <= 0) {
        handleComplete()
      }
    }, 1000)
  }

  /**
   * Stops the countdown timer
   */
  function stop() {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
    isActive = false
    remainingSeconds = 0
  }

  /**
   * Pauses the countdown timer
   */
  function pause() {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
    isActive = false
  }

  /**
   * Resumes the countdown timer
   */
  function resume() {
    if (isActive || remainingSeconds <= 0) {
      return
    }

    isActive = true
    timer = setInterval(() => {
      remainingSeconds--

      // Call tick callback
      if (callbacks.onTick) {
        callbacks.onTick(remainingSeconds, formatTime(remainingSeconds))
      }

      // Check if timer is complete
      if (remainingSeconds <= 0) {
        handleComplete()
      }
    }, 1000)
  }

  /**
   * Handles timer completion
   */
  function handleComplete() {
    stop()
    
    if (callbacks.onComplete) {
      callbacks.onComplete()
    }
  }

  /**
   * Gets the current remaining time
   * @returns {Object} Current timer state
   */
  function getState() {
    return {
      remainingSeconds,
      formattedTime: formatTime(remainingSeconds),
      isActive,
      isComplete: remainingSeconds <= 0
    }
  }

  /**
   * Adds time to the current countdown
   * @param {number} seconds - Seconds to add
   */
  function addTime(seconds) {
    remainingSeconds = Math.max(0, remainingSeconds + seconds)
    
    if (callbacks.onTick) {
      callbacks.onTick(remainingSeconds, formatTime(remainingSeconds))
    }
  }

  /**
   * Sets a new target time while keeping the timer running
   * @param {string|number|Date} targetTime - New target time
   */
  function updateTarget(targetTime) {
    const targetTimestamp = new Date(targetTime).getTime()
    const now = Date.now()
    remainingSeconds = Math.max(0, Math.floor((targetTimestamp - now) / 1000))

    if (remainingSeconds <= 0) {
      handleComplete()
    } else if (callbacks.onTick) {
      callbacks.onTick(remainingSeconds, formatTime(remainingSeconds))
    }
  }

  /**
   * Cleanup function to be called when component unmounts
   */
  function cleanup() {
    stop()
    callbacks.onTick = null
    callbacks.onComplete = null
  }

  // Return public API
  return {
    start,
    stop,
    pause,
    resume,
    getState,
    addTime,
    updateTarget,
    cleanup,
    formatTime
  }
}

/**
 * Vue 2 compatible mixin for countdown timer
 * 
 * Usage:
 * import { countdownTimerMixin } from '@/composables/useCountdownTimer'
 * 
 * export default {
 *   mixins: [countdownTimerMixin],
 *   mounted() {
 *     this.startCountdown(targetTime, {
 *       onTick: (seconds, formatted) => {
 *         this.remainingTime = formatted
 *       },
 *       onComplete: () => {
 *         this.handleTimerComplete()
 *       }
 *     })
 *   }
 * }
 */
export const countdownTimerMixin = {
  data() {
    return {
      countdownTimer: null
    }
  },

  created() {
    this.countdownTimer = useCountdownTimer()
  },
  
  beforeDestroy() {
    if (this.countdownTimer) {
      this.countdownTimer.cleanup()
    }
  },
  
  methods: {
    startCountdown(targetTime, options) {
      if (this.countdownTimer) {
        this.countdownTimer.start(targetTime, options)
      }
    },
    
    stopCountdown() {
      if (this.countdownTimer) {
        this.countdownTimer.stop()
      }
    },
    
    pauseCountdown() {
      if (this.countdownTimer) {
        this.countdownTimer.pause()
      }
    },
    
    resumeCountdown() {
      if (this.countdownTimer) {
        this.countdownTimer.resume()
      }
    },
    
    getCountdownState() {
      return this.countdownTimer ? this.countdownTimer.getState() : null
    }
  }
}
